import Fuse from 'fuse.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'

import { useActivityTagsState } from '../useActivityTagsState/useActivityTagsState'

import { ActivityTag } from '@percent/admin-dashboard/api/types'
import { DecoratedActivitySubTag } from './useActivityTags.types'

export const useActivityTags = ({ filterByEducation }: { filterByEducation?: boolean }) => {
  const {
    activityTags,
    isLoading: isActivityTagsLoading,
    errorMessage: activityTagsErrorMessage
  } = useActivityTagsState()
  const [searchActivityTagsValue, setSearchActivityTagsValue] = useState('')
  const [searchActivityTagsResult, setSearchActivityTagsResult] = useState<ActivityTag[]>([])

  const options = {
    includeMatches: true,
    ignoreLocation: true,
    keys: [
      { name: 'name', weight: 100 },
      { name: 'description', weight: 60 },
      { name: 'parentTagName', weight: 80 }
    ],
    threshold: 0.1
  }

  const flatActivitySubTags = activityTags?.flatMap(({ id, name, subTags }) =>
    subTags.map(subTag => ({ ...subTag, parentTagName: name, parentTagId: id }))
  )

  const activityTagsSearch = activityTags && new Fuse(flatActivitySubTags, options)

  const mapHitsToTags = (flattenedActivitySubTags: DecoratedActivitySubTag[]): ActivityTag[] => {
    const tags: Record<string, ActivityTag> = {}

    flattenedActivitySubTags?.forEach(hit => {
      tags[hit.parentTagName] = {
        subTags: !tags[hit.parentTagName] ? [hit] : [...tags[hit.parentTagName].subTags, hit],
        id: hit.parentTagId,
        name: hit.parentTagName
      }
    })

    return Object.values(tags)
  }

  const filterActivityTags = useCallback(
    (activityTagsArray: ActivityTag[]) => {
      let filteredActivityTags = activityTagsArray?.flatMap(({ id, name, subTags }) =>
        subTags.map(subTag => ({ ...subTag, parentTagName: name, parentTagId: id }))
      )

      if (filterByEducation) {
        filteredActivityTags = filteredActivityTags?.filter(({ isEducationStage }) => isEducationStage)
      }

      return mapHitsToTags(filteredActivityTags)
    },
    [filterByEducation]
  )

  const debouncedSetSearchActivityTagsResult = useCallback(debounce(setSearchActivityTagsValue, 600), [])

  useEffect(() => {
    if (searchActivityTagsValue) {
      const results = activityTagsSearch.search(searchActivityTagsValue)

      setSearchActivityTagsResult(
        mapHitsToTags(results.map(({ item }) => ({ ...item, highlightedWords: [searchActivityTagsValue] })))
      )
    } else {
      setSearchActivityTagsResult(activityTags)
    }
  }, [searchActivityTagsValue])

  useEffect(() => {
    if (activityTags) {
      setSearchActivityTagsResult(activityTags)
    }
  }, [activityTags])

  const filteredActivityTags = useMemo(
    () => filterActivityTags(searchActivityTagsResult),
    [searchActivityTagsResult, filterActivityTags]
  )

  return {
    activityTags: filteredActivityTags,
    isActivityTagsLoading,
    activityTagsErrorMessage,
    isActivityTagsDataFiltered: !!searchActivityTagsValue,
    setSearchActivityTagsValue: debouncedSetSearchActivityTagsResult,
    mapHitsToTags
  }
}
